import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../shared/api/http';
import type {
  AliasCheck,
  AliasSuggestions,
  DeletedListData,
  DomainsData,
  Mailbox,
  MailboxListData,
  Quota,
} from '../../shared/api/types';

export const mailboxKeys = {
  all: ['mailboxes'] as const,
  deleted: ['mailboxes', 'deleted'] as const,
  one: (id: string) => ['mailbox', id] as const,
  quota: ['quota'] as const,
  domains: ['domains'] as const,
  aliasCheck: (alias: string, domain: string) => ['aliasCheck', alias, domain] as const,
  aliasSuggestions: (domain: string) => ['aliasSuggestions', domain] as const,
};

export function useMailboxes(enabled = true) {
  return useQuery({
    queryKey: mailboxKeys.all,
    queryFn: () => request<MailboxListData>('/api/v1/mailboxes', { query: { limit: 100 } }),
    staleTime: 30_000,
    enabled,
  });
}

export function useDeletedMailboxes(enabled = true) {
  return useQuery({
    queryKey: mailboxKeys.deleted,
    queryFn: () => request<DeletedListData>('/api/v1/mailboxes/deleted'),
    staleTime: 30_000,
    enabled,
  });
}

export function useCleanupSuggestions(enabled: boolean) {
  return useQuery({
    queryKey: ['cleanupSuggestions'],
    queryFn: () => request<DeletedListData>('/api/v1/mailboxes/cleanup-suggestions'),
    enabled,
    staleTime: 0,
  });
}

export function useMailbox(id: string) {
  return useQuery({
    queryKey: mailboxKeys.one(id),
    queryFn: () => request<Mailbox>(`/api/v1/mailboxes/${id}`),
    staleTime: 60_000,
  });
}

export function useQuota(enabled = true) {
  return useQuery({
    queryKey: mailboxKeys.quota,
    queryFn: () => request<Quota>('/api/v1/quota'),
    staleTime: 60_000,
    enabled,
  });
}

export function useDomains() {
  return useQuery({
    queryKey: mailboxKeys.domains,
    queryFn: () => request<DomainsData>('/api/v1/domains'),
    staleTime: 60 * 60_000,
  });
}

export function useAliasCheck(alias: string, domain: string, enabled: boolean) {
  return useQuery({
    queryKey: mailboxKeys.aliasCheck(alias, domain),
    queryFn: () =>
      request<AliasCheck>('/api/v1/aliases/check', { query: { alias, domain } }),
    enabled: enabled && alias.length >= 3 && domain.length > 0,
    staleTime: 0,
    retry: false,
  });
}

export function useAliasSuggestions(domain: string) {
  return useMutation({
    mutationFn: (count: number) =>
      request<AliasSuggestions>('/api/v1/aliases/suggestions', { query: { domain, count } }),
  });
}

function useInvalidateMailboxes() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: mailboxKeys.all });
    void qc.invalidateQueries({ queryKey: mailboxKeys.quota });
    void qc.invalidateQueries({ queryKey: mailboxKeys.deleted });
  };
}

export interface CreateMailboxInput {
  alias: string;
  domain: string;
  description?: string;
}

export function useCreateMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (input: CreateMailboxInput) =>
      request<Mailbox>('/api/v1/mailboxes', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export interface EnsureFirstMailboxResult {
  created: boolean;
  mailbox: Mailbox | null;
}

/** Автосоздание первого ящика при первом входе (promt-81 §Q4) — на бэкенде идемпотентно. */
export function useEnsureFirstMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: () =>
      request<EnsureFirstMailboxResult>('/api/v1/mailboxes/ensure-first', { method: 'POST' }),
    onSuccess: (data) => {
      if (data.created) invalidate();
    },
  });
}

export function useDeleteMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (id: string) => request(`/api/v1/mailboxes/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useBulkDeleteMailboxes() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (ids: string[]) =>
      request('/api/v1/mailboxes/bulk-delete', { method: 'POST', body: { mailboxIds: ids } }),
    onSuccess: invalidate,
  });
}

export function useRestoreMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (id: string) =>
      request<Mailbox>(`/api/v1/mailboxes/${id}/restore`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export interface RestoreResult {
  ok: string[];
  failed: string[];
}

export function useRestoreMailboxes() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    // Восстанавливаем каждый ящик независимо: конфликт по одному адресу (напр. дубликат в
    // корзине) не должен блокировать остальные (promt-74 §6).
    mutationFn: async (ids: string[]): Promise<RestoreResult> => {
      const results = await Promise.allSettled(
        ids.map((id) => request<Mailbox>(`/api/v1/mailboxes/${id}/restore`, { method: 'POST' })),
      );
      const ok: string[] = [];
      const failed: string[] = [];
      results.forEach((r, i) => {
        (r.status === 'fulfilled' ? ok : failed).push(ids[i]);
      });
      return { ok, failed };
    },
    onSuccess: invalidate,
  });
}

export function usePinMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (id: string) =>
      request<Mailbox>(`/api/v1/mailboxes/${id}/pin`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useUnpinMailbox() {
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (id: string) =>
      request<Mailbox>(`/api/v1/mailboxes/${id}/unpin`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export interface UpdateMailboxInput {
  id: string;
  description?: string | null;
  forwardingEnabled?: boolean;
}

export function useUpdateMailbox() {
  const qc = useQueryClient();
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: ({ id, description, forwardingEnabled }: UpdateMailboxInput) =>
      request<Mailbox>(`/api/v1/mailboxes/${id}`, {
        method: 'PATCH',
        body: { description, forwardingEnabled },
      }),
    onSuccess: (data) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: mailboxKeys.one(data.id) });
    },
  });
}

export function useActivateMailbox() {
  const qc = useQueryClient();
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (id: string) =>
      request<Mailbox>(`/api/v1/mailboxes/${id}/activate`, { method: 'POST' }),
    onSuccess: (data) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: mailboxKeys.one(data.id) });
    },
  });
}

export function useExtendMailbox() {
  const qc = useQueryClient();
  const invalidate = useInvalidateMailboxes();
  return useMutation({
    mutationFn: (id: string) =>
      request<Mailbox>(`/api/v1/mailboxes/${id}/extend`, { method: 'POST' }),
    onSuccess: (data) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: mailboxKeys.one(data.id) });
    },
  });
}
